import { forwardRef } from "react";
import type { FieldValues, SubmitErrorHandler, SubmitHandler, UseFormReturn } from "react-hook-form";
import { FormProvider } from "react-hook-form";

type FormProps<V extends FieldValues> = {
  form: UseFormReturn<V>;
  children: React.ReactNode;
  onSubmit?: SubmitHandler<V>;
  onInvalid?: SubmitErrorHandler<V>;
  onKeyDown?: (e: React.KeyboardEvent<Element>) => void;
  dataTestId?: string;
  className?: string;
  id?: string;
  "aria-label"?: string;
  noValidate?: boolean;
};

const FormComponent = <V extends FieldValues>(
  {
    form,
    onSubmit,
    onInvalid,
    children,
    dataTestId,
    onKeyDown,
    className,
    id,
    "aria-label": ariaLabel,
    noValidate = true,
  }: FormProps<V>,
  ref: React.Ref<HTMLFormElement>
): JSX.Element => {
  const { handleSubmit } = form;

  const onKeyDownHandler = (e: React.KeyboardEvent<Element>) => onKeyDown?.(e);

  return (
    <form
      data-testid={dataTestId}
      onSubmit={onSubmit && handleSubmit(onSubmit, onInvalid)}
      onKeyDown={onKeyDownHandler}
      className={className}
      id={id}
      aria-label={ariaLabel}
      noValidate={noValidate}
      ref={ref}
    >
      <FormProvider {...form}>{children}</FormProvider>
    </form>
  );
};

export const Form = forwardRef(FormComponent) as <V extends FieldValues>(
  props: FormProps<V> & { ref?: React.Ref<HTMLFormElement> }
) => ReturnType<typeof FormComponent>;
